import { EInvoiceStatus } from "../../../enums";
import { ICityPendingInvoiceFile, IDuplicatedInvoices, IInvoice } from "../../../interfaces";
import { TypeSortCompany } from "../../../pages/CityCompanyInvoices/useCityCompanyInvoices";
import { TypeSortInvoice } from "../../../pages/CityInvoices/useCityInvoices";
import { IInvoiceDetails } from "../../../pages/InvoiceDetails/interfaces";
import { urlHttpGov } from "../../urls";
import CityPendingInvoiceFileMapper from "../mappers/CityPendingInvoiceFileMapper";
import privateHttpClient from "../privateHttpClient";

export interface IInvoicesTotal {
    month: number;
    reward_points: number;
    total_value: number;
    year: number;
    total_invoices: number;
    warning_invoices: number;
    completed_invoices: number;
    other_invoices: number;
}

export interface IInvoiceWithFilters {
    id: number,
    date_issue: Date,
    created_at: Date,
    type: 'NFC' | 'NFE' | 'NFS',
    key: number,
    number: number,
    authenticity_code: number,
    status: EInvoiceStatus,
    link: string,
    app_user_id: number,
    app_user_name: string,
    message: string,
    company: string,
    value: number,
    tickets: number[],
    city_issue?: string,
    state_issue?: string,
}

export interface ICompanyWithStats {
    company: string,
    cpfCnpj: number,
    total_value: number,
    total_invoice: number,
    completed_invoices: number,
    error_invoices: number,
    warning_invoices: number
}

interface IPropsListInvoicesWithPaginate {
    cityId: number,
    createdAt?: string,
    dateIssue?: string,
    date_issue?: string,
    startPeriod?: string,
    endPeriod?: string,
    type?: string,
    user?: string,
    company?: string,
    status?: string[],
    sort?: TypeSortInvoice | string;
    order?: 'asc' | 'desc',
    page: number;
    size: number;
    signal: AbortSignal
}


interface IPropsListCompanyWithPaginate {
    cityId: number,
    company?: string,
    cpfCnpj?: string,
    sort?: TypeSortCompany;
    order?: 'asc' | 'desc',
    page: number;
    size: number;
    signal: AbortSignal
}

interface IPropsValuesInvoice {
    porStatus: {
        status: EInvoiceStatus,
        total: number,
        sum: number,
    }[],
    total: number
}

class InvoicesService {
    private baseURL = `${urlHttpGov}/panel/invoices`;
    private baseURLFile: string = `${urlHttpGov}/panel/pending-invoice-files`;

    async listAllByCity({ cityId, page, size, sort, order, createdAt, dateIssue, date_issue, startPeriod, endPeriod, user, company, status, type, signal }: IPropsListInvoicesWithPaginate): Promise<{ invoices: IInvoiceWithFilters[], total: number }> {

        const params: Record<string, any> = {
            page,
            size,
            sort,
            order,
            createdAt,
            dateIssue,
            date_issue,
            startPeriod,
            endPeriod,
            user,
            company,
            status,
            type
        };

        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        const queryString = new URLSearchParams(params).toString();
        const url = `${this.baseURL}/${cityId}/paginate?${queryString}`;

        const { data } = await privateHttpClient.get(url, { signal });

        return data;
    }


    async findValuesInvoices({ cityId, startPeriod, endPeriod, signal }: { cityId: number, startPeriod: string, endPeriod: string, signal: AbortSignal }): Promise<IPropsValuesInvoice> {

        const params: Record<string, any> = {
            startPeriod,
            endPeriod,
        };

        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        const queryString = new URLSearchParams(params).toString();
        const url = `${this.baseURL}/${cityId}/values?${queryString}`;

        const { data } = await privateHttpClient.get(url, { signal });

        return data;
    }

    async listAllCompanyByCity({ cityId, company, cpfCnpj, page, size, sort, order, signal }: IPropsListCompanyWithPaginate): Promise<{ companies: ICompanyWithStats[], total: number }> {

        const params: Record<string, any> = {
            company,
            cpfCnpj,
            page,
            size,
            sort,
            order,
        };

        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        const queryString = new URLSearchParams(params).toString();
        const url = `${this.baseURL}/${cityId}/company/paginate?${queryString}`;

        const { data } = await privateHttpClient.get(url, { signal });

        return data;

    }


    async listMonthsData({ cityId, appUserId, signal }: { cityId: number, appUserId: number, signal: AbortSignal }): Promise<IInvoicesTotal[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/group/${appUserId}/${cityId}`, { signal });

        return data;
    }

    async listInvoicesByMonth({ cityId, appUserId, month, year, signal }: { cityId: number, appUserId: number, month: number, year: number, signal: AbortSignal }): Promise<IInvoice[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/details/${appUserId}/${cityId}?month=${month}&year=${year}`, { signal });

        return data;
    }

    async listInvoicesInProcessing({ cityId, appUserId, signal }: { cityId: number, appUserId: number, signal: AbortSignal }): Promise<IInvoice[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/in-processing/${appUserId}/${cityId}`, { signal });

        return data;
    }

    async listInvoicesAwaiting({ cityId, signal }: { cityId: number, signal: AbortSignal }): Promise<IInvoice[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${cityId}/pending`, { signal });

        return data;
    }

    async listInvoicesDuplicated({ cityId, signal }: { cityId: number, signal: AbortSignal }): Promise<IDuplicatedInvoices[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${cityId}/duplicated`, { signal });

        return data;
    }

    async reprocessInvoice({ invoiceId, appUserId, cityId }: { invoiceId: number; appUserId: number; cityId: number; }): Promise<IInvoice> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/reprocessing/${invoiceId}`, {
            app_user_id: appUserId,
            county_id: cityId,
        });

        return data;
    }

    async listCityPendingInvoiceFile(cityId: number, signal: AbortSignal): Promise<Array<ICityPendingInvoiceFile>> {
        const { data } = await privateHttpClient.get(`${this.baseURLFile}/county/${cityId}`, { signal });

        return data.map((invoice: any) => CityPendingInvoiceFileMapper.toDomain(invoice));
    }

    async createPendingInvoice(pendingInvoice: FormData): Promise<string> {

        const { data } = await privateHttpClient.post(`${this.baseURLFile}/import`, pendingInvoice, {
            headers: {
                "Content-Type": 'multipart/form-data'
            }
        });

        return data.message;
    }

    async downloadPendingInvoiceFile(file_name: string, signal: AbortSignal): Promise<string> {
        const { data } = await privateHttpClient.get(`${this.baseURLFile}/download/${file_name}`, { signal });

        return data.fileUrl;
    }

    async getInvoiceById(id: number, signal: AbortSignal): Promise<IInvoiceDetails> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${id}/details`, { signal });

        return data;
    }

    async listAllInvoicesByPeriodAndAppUserId({ cityId, appUserId, startPeriod, endPeriod, signal }: { cityId: number, appUserId: number, startPeriod: string, endPeriod: string, signal: AbortSignal }): Promise<IInvoice[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/period/app_user/${appUserId}?countyId=${cityId}&startPeriod=${startPeriod}&endPeriod=${endPeriod}`, { signal });

        return data;
    }

    async updateInvoiceStatus(id: number, params: { status: EInvoiceStatus; message: string; deleteTickets: boolean; manuallyValidated: boolean }) {
        const { data } = await privateHttpClient.put(
            `${this.baseURL}/${id}/status`,
            {
                status: params.status,
                message: params.message,
                delete_tickets: params.deleteTickets,
                manually_validated: params.manuallyValidated
            }
        );

        return data;
    }

}

// eslint-disable-next-line import/no-anonymous-default-export
export default new InvoicesService();
